/**
 * PGC Full Analysis overlay (2026-10-22).
 *
 * Draws PGC's OWN corner/edge/centering grades on the straightened card crop
 * (_images.clean) — replacing Ximilar's overlay images, whose burned-in
 * numbers contradicted the PGC headline (record 10923: image said Final 7.5 /
 * Surface 6.5 under a PGC 9). Tap a corner or edge for its zoom crop + note.
 *
 * Shared by both renderers: ximilar-results.js (live result) and
 * ResultRenderer.php (reload/history/share) — PHP emits
 * <div data-pgc-overlay='{record subset}'> and this script mounts it.
 * Only records stamped grade_pgc.overlay.v at grade time (api/grade-v3.php →
 * PgcOverlay::prepare) qualify; older records keep their original images.
 */
(function () {
    if (window.PGCOverlay) return;

    var CSS = ''
        + '.pgc-ov-card{background:var(--xg-panel,#fff);border-radius:8px;padding:20px;box-shadow:var(--xg-shadow,none);margin-bottom:16px;border:1px solid var(--xg-border,#e2e8f0);}'
        + '.pgc-ov-card h3{font-size:19px;font-weight:800;color:var(--xg-text,#0f172a);margin:0 0 4px;letter-spacing:-.01em;}'
        + '.pgc-ov-sub{font-size:13px;color:var(--xg-muted,#64748b);margin:0 0 14px;line-height:1.5;}'
        // Card beside its detail panel whenever there's room (wraps when narrow).
        + '.pgc-ov-body{display:flex;flex-wrap:wrap;gap:12px;align-items:flex-start;}'
        + '.pgc-ov-stage{background:#eef2f7;border-radius:12px;padding:6px;display:flex;justify-content:center;flex:0 1 272px;margin:0 auto;}'
        + '.pgc-ov-stage svg{display:block;width:100%;max-width:260px;height:auto;}'
        + '.pgc-ov-side{flex:1 1 220px;min-width:0;}'
        + '.pgc-ov-side .pgc-ov-detail{margin-top:0;}'
        // Default "Close-up view" placeholder: same footprint as the filled
        // close-up (.pgc-ov-detail) so nothing shifts when a zone is tapped.
        + '.pgc-ov-hint{display:flex;gap:12px;align-items:center;padding:10px;border:1.5px dashed var(--xg-border,#cbd5e1);border-radius:12px;background:var(--xg-subtle,#f8fafc);}'
        + '.pgc-ov-hint-ic{width:76px;height:76px;flex-shrink:0;border-radius:10px;background:var(--xg-panel,#fff);border:1px solid var(--xg-border,#e2e8f0);display:flex;align-items:center;justify-content:center;color:#2563eb;}'
        + '.pgc-ov-hint .t{font-size:12.5px;line-height:1.5;color:var(--xg-muted,#64748b);}'
        + '.pgc-ov-hint .t b{display:block;font-size:14px;font-weight:800;color:var(--xg-text,#0f172a);margin-bottom:2px;}'
        + '.pgc-ov-hint-c{animation:pgcOvPulse 2.4s ease-in-out infinite;}'
        + '.pgc-ov-hint-e{animation:pgcOvPulse 2.4s ease-in-out 1.2s infinite;}'
        + '@keyframes pgcOvPulse{0%,100%{opacity:.35;}50%{opacity:1;}}'
        + '@media (prefers-reduced-motion:reduce){.pgc-ov-hint-c,.pgc-ov-hint-e{animation:none;}}'
        + '.pgc-ov-detail.on + .pgc-ov-hint{display:none;}'

        + '.pgc-ov-stage svg text{font-family:Inter,system-ui,-apple-system,sans-serif;}'
        + '.pgc-ov-orig{cursor:zoom-in;outline:none;}'
        + '.pgc-ov-orig-bg{fill:transparent;transition:fill .15s;}'
        + '.pgc-ov-orig-pill{opacity:0;transition:opacity .15s;pointer-events:none;}'
        + '.pgc-ov-orig:hover .pgc-ov-orig-bg,.pgc-ov-orig:focus-visible .pgc-ov-orig-bg{fill:rgba(15,23,42,.12);}'
        + '.pgc-ov-orig:hover .pgc-ov-orig-pill,.pgc-ov-orig:focus-visible .pgc-ov-orig-pill{opacity:1;}'
        + '.pgc-ov-hit{fill:transparent;cursor:pointer;outline:none;}'
        + '.pgc-ov-hit:hover,.pgc-ov-hit:focus-visible,.pgc-ov-hit.sel{fill:rgba(37,99,235,.14);stroke:#2563eb;}'
        + '.pgc-ov-meta{display:flex;flex-wrap:wrap;gap:6px 14px;margin-top:12px;font-size:13px;color:var(--xg-text-2,#334155);}'
        + '.pgc-ov-meta b{color:var(--xg-text,#0f172a);}'
        + '.pgc-ov-legend{display:flex;flex-wrap:wrap;gap:10px;font-size:11.5px;color:var(--xg-muted,#64748b);margin-top:8px;}'
        + '.pgc-ov-legend i{display:inline-block;width:9px;height:9px;border-radius:50%;margin-right:4px;vertical-align:-1px;}'
        + '.pgc-ov-detail{display:none;margin-top:12px;padding:12px;border:1px solid var(--xg-border,#e2e8f0);border-radius:12px;background:var(--xg-subtle,#f8fafc);gap:12px;align-items:flex-start;}'
        + '.pgc-ov-detail.on{display:flex;}'
        + '.pgc-ov-detail img{width:96px;height:96px;object-fit:cover;border-radius:10px;border:1px solid var(--xg-border,#e2e8f0);flex-shrink:0;background:#fff;}'
        + '.pgc-ov-zoom{position:relative;flex-shrink:0;cursor:zoom-in;border-radius:10px;}'
        + '.pgc-ov-zoom:hover img,.pgc-ov-zoom:focus-visible img{border-color:#2563eb;box-shadow:0 0 0 2px rgba(37,99,235,.25);}'
        + '.pgc-ov-zoom-ic{position:absolute;right:5px;bottom:5px;width:20px;height:20px;border-radius:50%;background:rgba(15,23,42,.72);color:#fff;display:flex;align-items:center;justify-content:center;pointer-events:none;}'
        + '.pgc-ov-detail .t{font-size:13px;line-height:1.5;color:var(--xg-text-2,#334155);}'
        + '.pgc-ov-detail .t b{display:block;font-size:15px;margin-bottom:2px;}'
        + '.pgc-ov-detail .t .k{display:block;font-size:10.5px;font-weight:700;letter-spacing:.05em;text-transform:uppercase;color:var(--xg-muted,#64748b);margin:4px 0 1px;}'
        // AI Grading Results hero (ResultRenderer::renderPgcHero / ximilar-results.js renderPgcHero).
        + '.pgc-hero{container-type:inline-size;}'
        // Top strip: compact final grade beside the title.
        + '.pgc-hero-top{display:flex;align-items:center;gap:14px;}'
        + '.pgc-hero-top .xg-ring{width:78px;height:78px;flex-shrink:0;margin:0;}'
        + '.pgc-hero-top .xg-ring-inner{width:calc(100% - 10px);height:calc(100% - 10px);padding:0;}'
        + '.pgc-hero-top .xg-ring-grade{font-size:28px;}'
        + '.pgc-hero-head{min-width:0;flex:1;text-align:left;}'
        + '.pgc-hero-title{font-size:19px;font-weight:800;color:var(--xg-text,#0f172a);letter-spacing:-.01em;line-height:1.25;}'
        + '.pgc-hero-badge{display:inline-block;margin-left:8px;font-size:10px;font-weight:800;color:#059669;background:#ecfdf5;border:1px solid #a7f3d0;padding:2px 8px;border-radius:999px;vertical-align:middle;letter-spacing:.04em;}'
        + '.pgc-hero-cond{font-size:12px;font-weight:800;letter-spacing:.12em;text-transform:uppercase;margin-top:3px;}'
        + '.pgc-hero-cond span{margin-left:8px;font-size:10px;font-weight:600;letter-spacing:.06em;color:var(--xg-muted,#64748b);}'
        + '.pgc-hero-fb{display:flex;gap:6px;flex-wrap:wrap;margin-top:6px;}'
        + '.pgc-hero-fb .xim-chip{font-size:12px;padding:3px 10px;}'
        + '.pgc-hero-sub{font-size:12.5px;color:var(--xg-muted,#64748b);margin:10px 0 14px;line-height:1.5;}'
        // Card left; close-up (top) + pillars right. Stacks only when the panel itself is narrow.
        + '.pgc-hero-grid{display:grid;grid-template-columns:minmax(160px,250px) minmax(0,1fr);gap:16px;align-items:start;}'
        + '.pgc-hero .pgc-ov-stage{align-items:center;}'
        + '.pgc-hero-side{min-width:0;}'
        + '.pgc-hero .pgc-ov-detail{margin:0 0 10px;padding:10px;gap:10px;}'
        + '.pgc-hero .pgc-ov-detail img{width:76px;height:76px;}'
        + '.pgc-hero .pgc-ov-detail .t{font-size:12.5px;}'
        + '.pgc-hero .pgc-ov-detail .t b{font-size:14px;}'
        + '.pgc-hero .pgc-ov-hint{margin-bottom:10px;}'
        + '.pgc-hero-label{font-size:10.5px;font-weight:800;color:var(--xg-muted,#64748b);text-transform:uppercase;letter-spacing:.06em;margin-bottom:6px;}'
        + '.pgc-hero-pillars{display:grid;grid-template-columns:1fr 1fr;gap:8px;}'
        + '.pgc-pt{position:relative;padding:8px 10px 10px;border-radius:10px;background:var(--xg-subtle,#f8fafc);border:1px solid var(--xg-border,#e2e8f0);border-left:3px solid var(--c);overflow:hidden;display:flex;align-items:center;flex-wrap:wrap;gap:2px 8px;}'
        + '.pgc-pt-k{flex:1 1 100%;font-size:9.5px;font-weight:800;text-transform:uppercase;letter-spacing:.06em;color:var(--xg-muted,#64748b);}'
        + '.pgc-pt-v{font-size:20px;font-weight:800;line-height:1.1;color:var(--c);letter-spacing:-.02em;}'
        + '.pgc-pt .xg-sev{display:inline-block;width:auto;font-size:9px;font-weight:800;color:#fff;padding:2px 7px;border-radius:999px;letter-spacing:.03em;}'
        + '.pgc-pt-bar{position:absolute;left:0;right:0;bottom:0;height:3px;background:var(--xg-border,#e2e8f0);}'
        + '.pgc-pt-bar i{display:block;height:100%;background:var(--c);}'
        + '.pgc-hero .pgc-ov-meta{margin-top:10px;}'
        + '.pgc-hero-chips{margin-top:16px;padding-top:14px;border-top:1px solid var(--xg-border,#e2e8f0);width:100%;flex:1 1 100%;grid-column:1/-1;}'
        + '.pgc-hero-chips > div{margin-bottom:0 !important;}'
        + '.pgc-hero .ximilar-iq-caps,.pgc-hero > div[style*="xg-warn-soft"]{margin-top:14px !important;}'
        + '@container (max-width:440px){.pgc-hero-grid{grid-template-columns:1fr;}.pgc-hero .pgc-ov-stage svg{max-width:240px;}}'
        + '@media (max-width:520px){.pgc-hero{padding:16px !important;}.pgc-hero-title{font-size:17px;}}'
        + '@media print{.pgc-ov-hit{display:none;}}';

    // Default close-up placeholder — keep in sync with ResultRenderer::PGC_OV_HINT.
    var PGC_OV_HINT = '<div class="pgc-ov-hint">'
        + '<div class="pgc-ov-hint-ic" aria-hidden="true"><svg width="36" height="46" viewBox="0 0 36 46" fill="none">'
        + '<rect x="4" y="4" width="28" height="38" rx="3.5" stroke="currentColor" stroke-opacity=".3" stroke-width="2"/>'
        + '<path class="pgc-ov-hint-c" d="M4 14V7.5A3.5 3.5 0 0 1 7.5 4H14" stroke="currentColor" stroke-width="3.2" stroke-linecap="round"/>'
        + '<path class="pgc-ov-hint-e" d="M32 18v10" stroke="currentColor" stroke-width="3.2" stroke-linecap="round"/>'
        + '</svg></div>'
        + '<div class="t"><b>Close-up view</b>Select any corner or edge on the card to inspect it here, then tap the close-up to zoom in. Tap the middle of the card to see your original photo.</div>'
        + '</div>';

    function injectCss() {
        if (document.getElementById('pgc-ov-css')) return;
        var st = document.createElement('style');
        st.id = 'pgc-ov-css';
        st.textContent = CSS;
        document.head.appendChild(st);
    }

    function num(v) { var n = parseFloat(v); return isFinite(n) ? n : null; }
    // The report's gradeColor() palette (ResultRenderer / ximilar-results.js);
    // the 7+ lime is one shade deeper so white chip text stays legible.
    function col(g) { return g >= 9 ? '#10b981' : g >= 7 ? '#65a30d' : g >= 5 ? '#f59e0b' : '#ef4444'; }
    function fmt(g) { var n = num(g); return n == null ? '–' : String(Math.round(n * 10) / 10); }
    function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }

    /** Overlay data for a stamped PGC record, or null (caller keeps legacy images). */
    function fromRecord(r) {
        if (!r || r.grader !== 'pgc') return null;
        var gp = r.grade_pgc || {}, ov = gp.overlay, f = gp.front || {};
        if (!ov || !ov.v || !num(ov.w) || !num(ov.h)) return null;
        var im = r._images || {};
        var crop = im.clean || im.cropped;
        if (!crop) return null;
        var c = f.corners || {}, e = f.edges || {};
        var vals = [c.ul, c.ur, c.ll, c.lr, e.top, e.right, e.bottom, e.left];
        for (var i = 0; i < vals.length; i++) if (num(vals[i]) == null) return null;
        var centering = null, cm = r.centering || {}, fc = f.centering || {};
        if (ov.centering && Array.isArray(cm.bound_box) && cm.bound_box.length === 4) {
            centering = {
                box: cm.bound_box.map(Number),
                lr: Array.isArray(fc.lr) ? fc.lr.join('/') : (cm.left_right || ''),
                tb: Array.isArray(fc.tb) ? fc.tb.join('/') : (cm.top_bottom || ''),
                grade: num(fc.grade) != null ? num(fc.grade) : num((r.grades || {}).centering)
            };
        }
        return {
            crop: String(crop), w: num(ov.w), h: num(ov.h),
            corners: { ul: num(c.ul), ur: num(c.ur), ll: num(c.ll), lr: num(c.lr) },
            edges: { top: num(e.top), right: num(e.right), bottom: num(e.bottom), left: num(e.left) },
            notes: { corner: c.note || '', edge: e.note || '' },
            centering: centering,
            original: String(r.original_image_url || im.original || r._url || ''),
            zoom: {
                corners: Array.isArray(im.corner_details) && im.corner_details.length === 4 ? im.corner_details : null,
                edges: Array.isArray(im.edge_details) && im.edge_details.length === 4 ? im.edge_details : null
            }
        };
    }

    function chip(x, y, txt, fs, fill, round) {
        var w = txt.length * fs * 0.62 + fs * 0.9, h = fs * 1.45, r = round ? h / 2 : fs * 0.28;
        return '<rect x="' + (x - w / 2) + '" y="' + (y - h / 2) + '" width="' + w + '" height="' + h + '" rx="' + r + '" fill="' + fill + '" stroke="#fff" stroke-width="' + (fs * 0.12) + '"/>'
            + '<text x="' + x + '" y="' + (y + fs * 0.36) + '" font-size="' + fs + '" font-weight="800" text-anchor="middle" fill="#fff">' + esc(txt) + '</text>';
    }

    var NAMES = { ul: 'Top-left corner', ur: 'Top-right corner', ll: 'Bottom-left corner', lr: 'Bottom-right corner', top: 'Top edge', bottom: 'Bottom edge', left: 'Left edge', right: 'Right edge' };
    var CORNER_IDX = { ul: 0, ur: 1, ll: 2, lr: 3 };   // _images.corner_details order: TL, TR, BL, BR
    var EDGE_IDX = { left: 0, right: 1, top: 2, bottom: 3 }; // _images.edge_details order: L, R, T, B

    function svg(d) {
        var cw = d.w, ch = d.h, m = Math.min(cw, ch);
        // Sized for a ~260px render: chips stay legible (~10px text).
        var P = m * 0.17, fs = m * 0.064, sw = m * 0.014, L = m * 0.13;
        var s = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="' + [-P, -P, cw + 2 * P, ch + 2 * P].join(' ') + '" role="img" aria-label="Card with PGC corner, edge and centering grades">';
        s += '<image href="' + esc(d.crop) + '" x="0" y="0" width="' + cw + '" height="' + ch + '" preserveAspectRatio="none"/>';
        s += '<rect x="0" y="0" width="' + cw + '" height="' + ch + '" fill="none" stroke="rgba(15,23,42,.35)" stroke-width="' + (sw * 0.3) + '"/>';

        if (d.centering) {
            var b = d.centering.box, cc = '#38bdf8', a = fs * 0.72, my = ch / 2, mx = cw / 2;
            var lr = String(d.centering.lr || '').split('/'), tb = String(d.centering.tb || '').split('/');
            s += '<rect x="' + b[0] + '" y="' + b[1] + '" width="' + (b[2] - b[0]) + '" height="' + (b[3] - b[1]) + '" fill="none" stroke="' + cc + '" stroke-width="' + (sw * 0.45) + '" stroke-dasharray="' + (sw * 2.2) + ' ' + (sw * 1.4) + '"/>';
            [[0, my, b[0], my], [b[2], my, cw, my], [mx, 0, mx, b[1]], [mx, b[3], mx, ch]].forEach(function (t) {
                s += '<line x1="' + t[0] + '" y1="' + t[1] + '" x2="' + t[2] + '" y2="' + t[3] + '" stroke="' + cc + '" stroke-width="' + (sw * 0.6) + '"/>';
            });
            var dk = 'rgba(8,47,73,.88)';
            if (lr[0]) s += chip(b[0] + a * 2.2, my - a * 1.4, 'L ' + lr[0], a, dk, true);
            if (lr[1]) s += chip(b[2] - a * 2.2, my - a * 1.4, 'R ' + lr[1], a, dk, true);
            if (tb[0]) s += chip(mx + a * 2.8, b[1] + a * 1.3, 'T ' + tb[0], a, dk, true);
            if (tb[1]) s += chip(mx + a * 2.8, b[3] - a * 1.3, 'B ' + tb[1], a, dk, true);
        }

        var off = P * 0.3, rails = {
            top: [L + sw, -off, cw - L - sw, -off, cw / 2, -P * 0.62],
            bottom: [L + sw, ch + off, cw - L - sw, ch + off, cw / 2, ch + P * 0.62],
            left: [-off, L + sw, -off, ch - L - sw, -P * 0.55, ch / 2],
            right: [cw + off, L + sw, cw + off, ch - L - sw, cw + P * 0.55, ch / 2]
        };
        Object.keys(rails).forEach(function (k) {
            var r = rails[k], g = d.edges[k];
            s += '<line x1="' + r[0] + '" y1="' + r[1] + '" x2="' + r[2] + '" y2="' + r[3] + '" stroke="' + col(g) + '" stroke-width="' + (sw * 0.7) + '" stroke-linecap="round"/>';
            s += chip(r[4], r[5], fmt(g), fs * 0.82, col(g), true);
        });
        var cornerGap = m * 0.02;    // bracket floats off the card corner (was 0.004 → 0.012 — owner wants a touch more air)
        var corners = { ul: [0, 0, 1, 1], ur: [cw, 0, -1, 1], ll: [0, ch, 1, -1], lr: [cw, ch, -1, -1] };
        Object.keys(corners).forEach(function (k) {
            var c = corners[k], x = c[0], y = c[1], dx = c[2], dy = c[3], g = d.corners[k];
            var vx = x - dx * cornerGap, vy = y - dy * cornerGap;
            s += '<path d="M ' + vx + ' ' + (vy + dy * L) + ' L ' + vx + ' ' + vy + ' L ' + (vx + dx * L) + ' ' + vy + '" fill="none" stroke="' + col(g) + '" stroke-width="' + sw + '" stroke-linecap="round" stroke-linejoin="round"/>';
            // P*0.62 clears the corner by ~4% of the card's short side — at
            // P*0.5 the badge's inner edge landed ~2% INSIDE the card and the
            // chips read as stuck to the artwork (owner, 2026-10-29).
            var px = x - dx * P * 0.62, py = y - dy * P * 0.62;
            s += '<circle cx="' + px + '" cy="' + py + '" r="' + (fs * 0.95) + '" fill="' + col(g) + '" stroke="#fff" stroke-width="' + (fs * 0.12) + '"/>'
                + '<text x="' + px + '" y="' + (py + fs * 0.3) + '" font-size="' + (fs * 0.82) + '" font-weight="800" text-anchor="middle" fill="#fff">' + fmt(g) + '</text>';
        });

        // Middle of the card → original upload in the report's shared zoom
        // modal (.ximilar-zoomable, delegated click). Drawn BEFORE the
        // corner/edge targets so those keep priority where they overlap.
        if (d.original) {
            var pw = fs * 7.4, ph = fs * 1.7;
            s += '<g class="pgc-ov-orig ximilar-zoomable" data-zoom-src="' + esc(d.original) + '" data-zoom-label="Original photo" tabindex="0" role="button" aria-label="View the original photo">'
                + '<rect class="pgc-ov-orig-bg" x="0" y="0" width="' + cw + '" height="' + ch + '"/>'
                + '<g class="pgc-ov-orig-pill"><rect x="' + (cw / 2 - pw / 2) + '" y="' + (ch / 2 - ph / 2) + '" width="' + pw + '" height="' + ph + '" rx="' + (ph / 2) + '" fill="rgba(15,23,42,.78)"/>'
                + '<text x="' + (cw / 2) + '" y="' + (ch / 2 + fs * 0.3) + '" font-size="' + (fs * 0.8) + '" font-weight="700" text-anchor="middle" fill="#fff">View original</text></g>'
                + '</g>';
        }

        // Tap targets (keyboard-focusable).
        var z = L * 1.4, t = P + L * 0.6;
        [['ul', -P, -P, z + P, z + P], ['ur', cw - z, -P, z + P, z + P], ['ll', -P, ch - z, z + P, z + P], ['lr', cw - z, ch - z, z + P, z + P],
         ['top', z, -P, cw - 2 * z, t], ['bottom', z, ch - L * 0.6, cw - 2 * z, t], ['left', -P, z, t, ch - 2 * z], ['right', cw - L * 0.6, z, t, ch - 2 * z]
        ].forEach(function (h) {
            s += '<rect class="pgc-ov-hit" tabindex="0" role="button" aria-label="' + esc(NAMES[h[0]]) + '" data-k="' + h[0] + '" x="' + h[1] + '" y="' + h[2] + '" width="' + h[3] + '" height="' + h[4] + '" rx="' + (L * 0.15) + '" stroke-width="' + (L * 0.04) + '"/>';
        });
        return s + '</svg>';
    }

    function html(d) {
        injectCss();
        var meta = '';
        if (d.centering) {
            meta = '<div class="pgc-ov-meta"><span>Centering <b>' + fmt(d.centering.grade) + '</b></span>'
                + (d.centering.lr ? '<span>Left/Right <b>' + esc(d.centering.lr) + '</b></span>' : '')
                + (d.centering.tb ? '<span>Top/Bottom <b>' + esc(d.centering.tb) + '</b></span>' : '') + '</div>';
        }
        return '<div class="pgc-ov-card" data-pgc-ov=\'' + esc(JSON.stringify({ corners: d.corners, edges: d.edges, notes: d.notes, zoom: d.zoom })) + '\'>'
            + '<h3>Full Analysis</h3>'
            + '<p class="pgc-ov-sub">Our grade for every corner and edge, drawn on your card' + (d.centering ? ', with the measured centering borders' : '') + '.</p>'
            + '<div class="pgc-ov-body">'
            + '<div class="pgc-ov-stage">' + svg(d) + '</div>'
            + '<div class="pgc-ov-side">'
            + '<div class="pgc-ov-detail" aria-live="polite"></div>'
            + PGC_OV_HINT
            + meta
            + '<div class="pgc-ov-legend"><span><i style="background:#10b981"></i>9–10</span><span><i style="background:#65a30d"></i>7–8.5</span><span><i style="background:#f59e0b"></i>5–6.5</span><span><i style="background:#ef4444"></i>below 5</span></div>'
            + '</div></div>'
            + '</div>';
    }

    function showDetail(hit) {
        var card = hit.closest('[data-pgc-ov]');
        if (!card) return;
        var d; try { d = JSON.parse(card.getAttribute('data-pgc-ov')); } catch (_) { return; }
        var k = hit.getAttribute('data-k'), isCorner = k in CORNER_IDX;
        var g = isCorner ? d.corners[k] : d.edges[k];
        var zoomList = isCorner ? (d.zoom && d.zoom.corners) : (d.zoom && d.zoom.edges);
        var zoom = zoomList ? zoomList[isCorner ? CORNER_IDX[k] : EDGE_IDX[k]] : null;
        card.querySelectorAll('.pgc-ov-hit.sel').forEach(function (x) { x.classList.remove('sel'); });
        hit.classList.add('sel');
        var panel = card.querySelector('.pgc-ov-detail');
        // 320px thumb (same endpoint ximilar-results.js uses) — the detail
        // crops are ~1500px webp files, slow to appear on tap.
        var thumb = zoom ? '/api/img-thumb?src=' + encodeURIComponent(String(zoom).split('?')[0]) + '&w=320' : null;
        // .ximilar-zoomable → the report's shared lightbox (same zoom modal as
        // the Corner/Edge Details grids) opens the full-size crop on click.
        panel.innerHTML = (zoom ? '<div class="ximilar-zoomable pgc-ov-zoom" data-k="' + k + '" data-zoom-src="' + esc(zoom) + '" data-zoom-label="' + esc(NAMES[k] + ' — ' + fmt(g)) + '" title="Click to zoom" role="button" tabindex="0" aria-label="Enlarge ' + esc(NAMES[k]) + ' close-up">'
            + '<img src="' + esc(thumb) + '" alt="' + esc(NAMES[k]) + ' close-up" onerror="this.onerror=null;this.src=' + esc(JSON.stringify(String(zoom))) + '">'
            + '<span class="pgc-ov-zoom-ic" aria-hidden="true"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><circle cx="11" cy="11" r="7"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg></span></div>' : '')
            + '<div class="t"><b style="color:' + col(g) + '">' + esc(NAMES[k]) + ': ' + fmt(g) + '</b>'
            + ((isCorner ? d.notes.corner : d.notes.edge) ? '<span class="k">Grader\'s note on all ' + (isCorner ? 'corners' : 'edges') + '</span>' + esc(isCorner ? d.notes.corner : d.notes.edge) : '') + '</div>';
        panel.classList.add('on');
    }

    /**
     * Prev/next lineup for the shared zoom lightboxes (ximilar-results.js,
     * record-interactions.js) on stamped records, or null (caller keeps its
     * page-wide list).
     *  - Corner/edge close-up: all 8 crops starting at the clicked one
     *    (clockwise: corners, then edges), then the Analysis Images; wraps
     *    back to the clicked crop.
     *  - Analysis Images tile or "View original" on the card: just the
     *    Analysis Images (original, cropped, back).
     * Crops that aren't on the page are detached .ximilar-zoomable stand-ins
     * (the lightbox only reads data-zoom-*).
     */
    var LINEUP_ORDER = ['ul', 'ur', 'lr', 'll', 'top', 'right', 'bottom', 'left'];
    function zKey(el) { return String(el.getAttribute('data-zoom-src') || '').split('?')[0].replace(/\.(jpe?g|png|webp|avif)$/i, ''); }
    function lineup(target) {
        if (!target || !target.closest || !document.querySelector('[data-pgc-ov]')) return null;
        var zoomEl = target.closest('.pgc-ov-zoom');
        if (!zoomEl && !target.closest('.ximilar-image-item') && !target.closest('.pgc-ov-orig')) return null;
        var tKey = zKey(target), placed = false, seen = {};
        var gallery = Array.prototype.slice.call(document.querySelectorAll('.ximilar-image-item .ximilar-zoomable[data-zoom-src]')).filter(function (el) {
            return el.offsetParent !== null || el.getClientRects().length > 0;
        }).map(function (el) {
            if (!zoomEl && zKey(el) === tKey) { placed = true; return target; }
            return el;
        }).filter(function (el) {
            var k = zKey(el);
            if (seen[k]) return false;
            seen[k] = true;
            return true;
        });
        if (!zoomEl) { if (!placed) gallery.unshift(target); return gallery; }
        var card = zoomEl.closest('[data-pgc-ov]'), d = null;
        try { d = JSON.parse(card.getAttribute('data-pgc-ov')); } catch (_) {}
        var crops = [zoomEl], start = LINEUP_ORDER.indexOf(zoomEl.getAttribute('data-k'));
        if (d && d.zoom && start >= 0) {
            for (var i = 1; i < LINEUP_ORDER.length; i++) {
                var k = LINEUP_ORDER[(start + i) % LINEUP_ORDER.length], isCorner = k in CORNER_IDX;
                var list = isCorner ? d.zoom.corners : d.zoom.edges;
                var src = list ? list[isCorner ? CORNER_IDX[k] : EDGE_IDX[k]] : null;
                if (!src) continue;
                var el = document.createElement('div');
                el.className = 'ximilar-zoomable pgc-ov-zoom';
                el.setAttribute('data-k', k);
                el.setAttribute('data-zoom-src', String(src));
                el.setAttribute('data-zoom-label', NAMES[k] + ' — ' + fmt(isCorner ? d.corners[k] : d.edges[k]));
                // No thumb <img>: 7 eager /api/img-thumb requests per open
                // queued ahead of the image being viewed. The lightbox loads
                // the full crop directly and preloadNeighbors warms the next.
                crops.push(el);
            }
        }
        return crops.concat(gallery);
    }

    document.addEventListener('click', function (e) {
        var hit = e.target && e.target.closest ? e.target.closest('.pgc-ov-hit') : null;
        if (hit) showDetail(hit);
    });
    document.addEventListener('keydown', function (e) {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        var t = e.target;
        if (t && t.classList && t.classList.contains('pgc-ov-hit')) { e.preventDefault(); showDetail(t); }
        else if (t && t.classList && (t.classList.contains('pgc-ov-zoom') || t.classList.contains('pgc-ov-orig'))) {
            // SVG elements have no .click() — dispatch for the lightbox's delegated handler.
            e.preventDefault(); t.dispatchEvent(new MouseEvent('click', { bubbles: true }));
        }
    });

    /**
     * Mount every [data-pgc-overlay] node (record subset JSON from PHP or JS).
     * Hero markup (renderPgcHero) carries a .pgc-ov-slot for the card SVG plus
     * its own detail panel; a bare mount node gets the standalone panel.
     */
    function mountAll(root) {
        injectCss();
        (root || document).querySelectorAll('[data-pgc-overlay]:not([data-pgc-mounted])').forEach(function (el) {
            el.setAttribute('data-pgc-mounted', '1');
            var d = null;
            try { d = fromRecord(JSON.parse(el.getAttribute('data-pgc-overlay'))); } catch (_) {}
            if (!d) return;
            var slot = el.querySelector('.pgc-ov-slot');
            // Upgrade a plain fallback hint (rendered before this script loaded).
            var hint = el.querySelector('.pgc-ov-hint');
            if (hint && !hint.querySelector('.pgc-ov-hint-ic')) hint.outerHTML = PGC_OV_HINT;
            if (slot) {
                slot.innerHTML = svg(d);
                el.setAttribute('data-pgc-ov', JSON.stringify({ corners: d.corners, edges: d.edges, notes: d.notes, zoom: d.zoom }));
            } else {
                el.innerHTML = html(d);
            }
        });
    }

    window.PGCOverlay = { fromRecord: fromRecord, html: html, mountAll: mountAll, lineup: lineup, HINT: PGC_OV_HINT };
    injectCss(); // as early as possible — the hero is server-rendered before this runs
    // Results are often injected after load (grade-v3.js swaps in the
    // ?result= server render via innerHTML) — mount those too.
    function watch() {
        mountAll();
        if (!window.MutationObserver || !document.body) return;
        new MutationObserver(function (muts) {
            for (var i = 0; i < muts.length; i++) {
                var n = muts[i].addedNodes;
                for (var j = 0; j < n.length; j++) {
                    if (n[j].nodeType === 1 && (n[j].hasAttribute('data-pgc-overlay') || n[j].querySelector('[data-pgc-overlay]'))) { mountAll(); return; }
                }
            }
        }).observe(document.body, { childList: true, subtree: true });
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watch);
    else watch();
})();
